import { useCallback, useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Bell, PaperPlaneRight, Plus, Trash, ToggleLeft, ToggleRight } from "@phosphor-icons/react";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const to12 = (h) => { const h2 = Number(h) || 0; const m = h2 % 12; return m === 0 ? 12 : m; };
const amPm = (h) => (Number(h) >= 12 ? "PM" : "AM");
const from12 = (h12, ap) => { let h = Number(h12) % 12; if (ap === "PM") h += 12; return h; };

function scheduleLabel(s) {
  const day = DAY_LONG[s.day_of_week % 7];
  const hh12 = to12(s.hour); const ap = amPm(s.hour); const mm = String(s.minute).padStart(2, "0");
  return `${day} ${hh12}:${mm} ${ap} · matures within ${s.days_threshold} days`;
}

function humanTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
  } catch { return String(iso); }
}

export default function ReminderConfigModal({ open, onClose, isSuperAdmin }) {
  const [schedules, setSchedules] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [loading, setLoading] = useState(true);
  const [firing, setFiring] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState(newDraft());
  // Testing section state — recipients (default: none checked so the caller
  // must explicitly pick) and the schedule chosen for the N-day test run.
  const [testRecipients, setTestRecipients] = useState(new Set());
  const [testScheduleId, setTestScheduleId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/loans/reminders/schedules");
      const list = data?.schedules || [];
      setSchedules(list);
      setEligible(data?.eligible_recipients || []);
      // Auto-select the first schedule for the N-day test dropdown if the
      // current selection is stale.
      setTestScheduleId((prev) => (list.some((s) => s.id === prev) ? prev : (list[0]?.id || "")));
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);
  if (!open) return null;

  const patch = async (sid, body) => {
    try {
      await api.patch(`/loans/reminders/schedules/${sid}`, body);
      load();
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const remove = async (sid) => {
    if (!window.confirm("Delete this schedule? It will stop firing immediately.")) return;
    try {
      await api.delete(`/loans/reminders/schedules/${sid}`);
      toast.success("Schedule deleted");
      load();
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const saveNew = async () => {
    if (!draft.recipient_user_ids?.length) { toast.error("Pick at least one recipient"); return; }
    setCreating(true);
    try {
      await api.post("/loans/reminders/schedules", draft);
      toast.success("Schedule added");
      setDraft(newDraft());
      setShowNew(false);
      load();
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setCreating(false); }
  };

  const runTest = async ({ ignoreThreshold, thresholdDays }) => {
    const recipientIds = Array.from(testRecipients);
    if (!recipientIds.length) { toast.error("Pick at least one recipient in the testing section"); return; }
    setFiring(true);
    try {
      const body = { recipient_user_ids: recipientIds };
      if (!ignoreThreshold) body.days_threshold = thresholdDays;
      const { data } = await api.post("/loans/reminders/test-flagged", body);
      const scope = ignoreThreshold
        ? `every flagged loan (${data.flagged_loans} row${data.flagged_loans === 1 ? "" : "s"})`
        : `flagged loans maturing within ${thresholdDays} day${thresholdDays === 1 ? "" : "s"}`;
      toast.success(`Fired ${data.emails_sent} email${data.emails_sent === 1 ? "" : "s"} to ${recipientIds.length} recipient${recipientIds.length === 1 ? "" : "s"} for ${scope}`);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setFiring(false); }
  };

  const toggleTestRecipient = (uid) => {
    setTestRecipients((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const selectedTestSchedule = schedules.find((s) => s.id === testScheduleId);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="reminder-config-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell size={18} className="text-amber-500" /> Email reminder schedules
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
            <strong>How schedules work:</strong> add as many as you want — each one fires an email once a week for every loan flagged for reminders that matures within its own N-day window. Toggle any schedule on/off without deleting.
          </div>

          {/* Existing schedules */}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Active schedules</div>
            {loading ? (
              <div className="py-4 text-center text-sm text-slate-500">Loading…</div>
            ) : schedules.length === 0 ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-500 italic" data-testid="reminder-schedules-empty">
                No schedules yet — add one below to start receiving weekly reminders.
              </div>
            ) : (
              <div className="space-y-2" data-testid="reminder-schedules-list">
                {schedules.map((s) => (
                  <div key={s.id} className={`rounded-lg border p-3 ${s.enabled ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/50"}`} data-testid={`reminder-schedule-${s.id}`}>
                    <div className="flex items-start gap-2 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-slate-800">{s.name || scheduleLabel(s)}</div>
                        <div className="text-[11px] text-slate-600 mt-0.5">{scheduleLabel(s)}</div>
                        <div className="text-[10px] text-slate-500 mt-1">
                          Recipients: <span className="font-semibold text-slate-700">{(s.recipient_users || []).map((u) => u.name || u.email).join(", ") || "—"}</span>
                        </div>
                        {s.created_by_name ? (
                          <div className="text-[10px] text-slate-400 mt-0.5">Activated by {s.created_by_name} · {humanTime(s.created_at)}</div>
                        ) : null}
                      </div>
                      {isSuperAdmin && (
                        <div className="flex items-center gap-1 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => patch(s.id, { enabled: !s.enabled })}
                            className={`h-7 px-2 text-[10px] ${s.enabled ? "text-emerald-700 border-emerald-300 hover:bg-emerald-100" : "text-slate-500 border-slate-300"}`}
                            data-testid={`reminder-schedule-toggle-${s.id}`}
                          >
                            {s.enabled ? <ToggleRight size={12} className="mr-1" /> : <ToggleLeft size={12} className="mr-1" />}
                            {s.enabled ? "Enabled" : "Disabled"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => remove(s.id)}
                            className="h-7 px-2 text-[10px] text-rose-600 border-rose-200 hover:bg-rose-50"
                            data-testid={`reminder-schedule-delete-${s.id}`}
                          >
                            <Trash size={12} className="mr-1" /> Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add a schedule */}
          {isSuperAdmin && !showNew && (
            <Button
              variant="outline"
              onClick={() => setShowNew(true)}
              className="w-full border-dashed border-[#327de3] text-[#1f64c2] hover:bg-[#eaf2ff]"
              data-testid="reminder-new-open"
            >
              <Plus size={13} className="mr-1.5" /> Add another schedule
            </Button>
          )}

          {isSuperAdmin && showNew && (
            <div className="rounded-lg border border-slate-300 p-3 bg-white space-y-3" data-testid="reminder-new-form">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">New schedule</div>
              <label className="block text-[11px]">
                <span className="uppercase tracking-widest font-semibold text-slate-500">Name (optional)</span>
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Ryan's weekly 30-day recap" className="h-8 text-sm mt-1" data-testid="reminder-new-name" />
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <SmallSelect label="Day" value={draft.day_of_week} onChange={(v) => setDraft({ ...draft, day_of_week: Number(v) })} options={DAY_LABELS.map((d, i) => [i, d])} testId="reminder-new-day" />
                <SmallSelect label="Hour" value={to12(draft.hour)} onChange={(v) => setDraft({ ...draft, hour: from12(Number(v), amPm(draft.hour)) })} options={Array.from({ length: 12 }, (_, i) => [i + 1, i + 1])} testId="reminder-new-hour" />
                <SmallSelect label="Minute" value={draft.minute} onChange={(v) => setDraft({ ...draft, minute: Number(v) })} options={[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => [m, String(m).padStart(2, "0")])} testId="reminder-new-minute" />
                <SmallSelect label="AM/PM" value={amPm(draft.hour)} onChange={(v) => setDraft({ ...draft, hour: from12(to12(draft.hour), v) })} options={[["AM", "AM"], ["PM", "PM"]]} testId="reminder-new-ampm" />
              </div>
              <label className="block text-[11px]">
                <span className="uppercase tracking-widest font-semibold text-slate-500">Send if maturity is within N days</span>
                <Input type="number" min={1} max={365} value={draft.days_threshold} onChange={(e) => setDraft({ ...draft, days_threshold: Number(e.target.value) })} className="h-8 text-sm w-32 mt-1" data-testid="reminder-new-threshold" />
              </label>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Recipients</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
                  {eligible.map((u) => (
                    <label key={u.id} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded border cursor-pointer ${draft.recipient_user_ids?.includes(u.id) ? "bg-[#eaf2ff] border-[#327de3]" : "bg-white border-slate-200"}`}>
                      <input
                        type="checkbox"
                        checked={draft.recipient_user_ids?.includes(u.id) || false}
                        onChange={() => setDraft((d) => {
                          const set = new Set(d.recipient_user_ids || []);
                          if (set.has(u.id)) set.delete(u.id); else set.add(u.id);
                          return { ...d, recipient_user_ids: Array.from(set) };
                        })}
                        className="accent-[#327de3]"
                        data-testid={`reminder-new-recipient-${u.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{u.name || u.email}</div>
                        <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                      </div>
                    </label>
                  ))}
                  {eligible.length === 0 && <div className="text-[11px] text-slate-400 italic">No eligible recipients yet.</div>}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowNew(false); setDraft(newDraft()); }} disabled={creating}>Cancel</Button>
                <Button onClick={saveNew} disabled={creating} className="bg-[#327de3] hover:bg-[#1f64c2]" data-testid="reminder-new-save">
                  {creating ? "Saving…" : "Save schedule"}
                </Button>
              </div>
            </div>
          )}
          {isSuperAdmin && (
            <div className="rounded-lg border border-amber-300 bg-amber-50/50 p-3 space-y-3" data-testid="reminder-testing-section">
              <div className="flex items-center gap-2">
                <PaperPlaneRight size={13} className="text-amber-600" />
                <div className="text-[11px] uppercase tracking-widest font-bold text-amber-800">Testing</div>
              </div>
              <div className="text-[11px] text-amber-800">
                Send reminder emails right now to verify formatting or attachments. The email body still says "matures in N days" using the loan's real maturity date.
              </div>

              {/* Recipient selector */}
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1.5">Send test emails to</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
                  {eligible.map((u) => (
                    <label
                      key={u.id}
                      className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded border cursor-pointer ${testRecipients.has(u.id) ? "bg-white border-amber-400 ring-1 ring-amber-300" : "bg-white/60 border-slate-200"}`}
                    >
                      <input
                        type="checkbox"
                        checked={testRecipients.has(u.id)}
                        onChange={() => toggleTestRecipient(u.id)}
                        className="accent-amber-500"
                        data-testid={`reminder-test-recipient-${u.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{u.name || u.email}</div>
                        <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                      </div>
                    </label>
                  ))}
                  {eligible.length === 0 && <div className="text-[11px] text-slate-400 italic">No eligible recipients available.</div>}
                </div>
              </div>

              {/* Test a schedule */}
              <div className="rounded-md border border-amber-200 bg-white p-2.5 space-y-2" data-testid="reminder-test-schedule-block">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Test a schedule</div>
                <div className="text-[11px] text-slate-600">
                  Fires immediately using a saved schedule's <strong>N-day maturity window</strong>. Day-of-week is ignored — this is a test.
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <label className="flex-1 min-w-[200px] text-[11px]">
                    <span className="uppercase tracking-widest font-semibold text-slate-500">Schedule</span>
                    <select
                      value={testScheduleId}
                      onChange={(e) => setTestScheduleId(e.target.value)}
                      className="w-full mt-1 text-sm h-8 border border-slate-300 rounded-md px-2 bg-white"
                      data-testid="reminder-test-schedule-select"
                    >
                      {schedules.length === 0 && <option value="">— No schedules saved —</option>}
                      {schedules.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name || scheduleLabel(s)} ({s.days_threshold}d)
                        </option>
                      ))}
                    </select>
                  </label>
                  <Button
                    onClick={() => runTest({ ignoreThreshold: false, thresholdDays: selectedTestSchedule?.days_threshold || 30 })}
                    disabled={firing || !selectedTestSchedule || testRecipients.size === 0}
                    className="bg-amber-500 hover:bg-amber-600"
                    data-testid="reminder-test-schedule-run"
                  >
                    {firing ? "Sending…" : `Test within ${selectedTestSchedule?.days_threshold ?? "N"}d`}
                  </Button>
                </div>
              </div>

              {/* Test all now (no threshold) */}
              <div className="rounded-md border border-amber-200 bg-white p-2.5 space-y-2" data-testid="reminder-test-all-block">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Test all now</div>
                <div className="text-[11px] text-slate-600">
                  Ignores the maturity window entirely. Sends one email per <strong>every loan whose reminder flag is on</strong> to the recipients above.
                </div>
                <Button
                  onClick={() => runTest({ ignoreThreshold: true })}
                  disabled={firing || testRecipients.size === 0}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="reminder-test-all-now"
                >
                  <PaperPlaneRight size={13} className="mr-1.5" /> {firing ? "Sending…" : "Test All Now"}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function newDraft() {
  return { name: "", enabled: true, day_of_week: 3, hour: 9, minute: 0, days_threshold: 30, recipient_user_ids: [] };
}

function SmallSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="block text-[11px]">
      <span className="uppercase tracking-widest font-semibold text-slate-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 text-sm h-8 border border-slate-300 rounded-md px-2 bg-white"
        data-testid={testId}
      >
        {options.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
      </select>
    </label>
  );
}
